import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to manage pre-recorded MP3 audio files
 * Preloads all files for zero-latency playback during the game
 * Uses Web Audio API for volume control (supports values > 1.0)
 */
export const useAudioFiles = () => {
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [filesLoaded, setFilesLoaded] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const audioCache = useRef({});
  const audioContextRef = useRef(null);
  
  // Letters used in the game
  const LETTERS = ['c', 'h', 'k', 'l', 'q', 'r', 's', 't'];
  
  // Category words (must match gameConstants.js)
  const CATEGORY_WORDS = {
    animals: ['dog', 'cat', 'lion', 'tiger', 'bear', 'wolf', 'deer', 'mouse'],
    fruits: ['apple', 'pear', 'banana', 'grape', 'lemon', 'peach', 'cherry', 'mango'],
    tools: ['hammer', 'drill', 'saw', 'wrench', 'pliers', 'shovel', 'knife', 'axe'],
    colors: ['red', 'blue', 'green', 'yellow', 'purple', 'white', 'pink', 'brown'],
    vehicles: ['car', 'bus', 'truck', 'train', 'airplane', 'boat', 'bike', 'van'],
    sports: ['soccer', 'tennis', 'golf', 'hockey', 'boxing', 'football', 'baseball', 'swimming'],
    music: ['piano', 'guitar', 'drums', 'violin', 'flute', 'trumpet', 'harp', 'cello'],
    body: ['hand', 'foot', 'head', 'arm', 'leg', 'nose', 'ear', 'eye']
  };
  
  // Names used in the game
  const NAMES = ['anna', 'mike', 'sara', 'john', 'emma', 'paul', 'lucy', 'mark'];
  
  // Voices
  const VOICES = ['male1', 'male2', 'female1', 'female2'];
  
  useEffect(() => {
    // Initialize Web Audio context
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    const loadAudioFiles = async () => {
      const cache = {};
      let loaded = 0;
      
      // Calculate total files to load
      const letterCount = LETTERS.length * VOICES.length; // 8 × 4 = 32
      const voiceIdCount = VOICES.length; // 4
      const categoryCount = Object.values(CATEGORY_WORDS).flat().length * VOICES.length; // 64 × 4 = 256
      const namesCount = NAMES.length * VOICES.length; // 8 × 4 = 32
      const total = letterCount + voiceIdCount + categoryCount + namesCount; // 324
      
      setTotalFiles(total);
      
      // Helper to load a single audio file as ArrayBuffer
      const loadFile = async (path, cacheKey) => {
        try {
          const response = await fetch(path);
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          cache[cacheKey] = audioBuffer;
          loaded++;
          setFilesLoaded(loaded);
          return true;
        } catch (error) {
          console.warn(`Failed to load audio: ${path}`, error);
          return false;
        }
      };
      
      // Load all files
      const loadPromises = [];
      
      // Load letters for each voice
      for (const voice of VOICES) {
        for (const letter of LETTERS) {
          const path = `/audio/${voice}/letters/${letter}.mp3`;
          const key = `${voice}_letter_${letter}`;
          loadPromises.push(loadFile(path, key));
        }
      }
      
      // Load voice-id files
      for (const voice of VOICES) {
        const path = `/audio/${voice}/voice-id.mp3`;
        const key = `${voice}_voice_id`;
        loadPromises.push(loadFile(path, key));
      }
      
      // Load category words for each voice
      for (const voice of VOICES) {
        for (const [category, words] of Object.entries(CATEGORY_WORDS)) {
          for (const word of words) {
            const path = `/audio/${voice}/categories/${category}/${word}.mp3`;
            const key = `${voice}_category_${word}`;
            loadPromises.push(loadFile(path, key));
          }
        }
      }
      
      // Load names for each voice
      for (const voice of VOICES) {
        for (const name of NAMES) {
          const path = `/audio/${voice}/names/${name}.mp3`;
          const key = `${voice}_name_${name}`;
          loadPromises.push(loadFile(path, key));
        }
      }
      
      // Wait for all files to load (or fail)
      await Promise.allSettled(loadPromises);
      
      audioCache.current = cache;
      setLoadingStatus('ready');
      
      console.log(`Audio files loaded: ${Object.keys(cache).length}/${total}`);
    };
    
    loadAudioFiles();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // Helper function to play audio with Web Audio API (supports volume > 1.0)
  const playAudioBuffer = useCallback((audioBuffer, volumeMultiplier = 1.0) => {
    if (!audioContextRef.current || !audioBuffer) return false;
    
    const ctx = audioContextRef.current;
    
    // Resume context if suspended (browsers require user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    // Create source and gain node
    const source = ctx.createBufferSource();
    const gainNode = ctx.createGain();
    
    source.buffer = audioBuffer;
    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Set volume (can be > 1.0!)
    gainNode.gain.value = volumeMultiplier;
    
    // Play
    source.start(0);
    
    return true;
  }, []);
  
  // Play a letter with specific voice
  const playLetter = useCallback((letter, voice, volumeMultiplier = 1.0) => {
    const key = `${voice}_letter_${letter.toLowerCase()}`;
    const audioBuffer = audioCache.current[key];
    
    if (audioBuffer) {
      return playAudioBuffer(audioBuffer, volumeMultiplier);
    }
    
    return false; // File not available, fallback to TTS
  }, [playAudioBuffer]);
  
  // Play voice-id phrase
  const playVoiceId = useCallback((voice, volumeMultiplier = 1.0) => {
    const key = `${voice}_voice_id`;
    const audioBuffer = audioCache.current[key];
    
    if (audioBuffer) {
      return playAudioBuffer(audioBuffer, volumeMultiplier);
    }
    
    return false;
  }, [playAudioBuffer]);
  
  // Play category word
  const playCategoryWord = useCallback((word, voice, volumeMultiplier = 1.0) => {
    const key = `${voice}_category_${word.toLowerCase()}`;
    const audioBuffer = audioCache.current[key];
    
    if (audioBuffer) {
      return playAudioBuffer(audioBuffer, volumeMultiplier);
    }
    
    return false;
  }, [playAudioBuffer]);
  
  // Play name
  const playName = useCallback((name, voice, volumeMultiplier = 1.0) => {
    const key = `${voice}_name_${name.toLowerCase()}`;
    const audioBuffer = audioCache.current[key];
    
    if (audioBuffer) {
      return playAudioBuffer(audioBuffer, volumeMultiplier);
    }
    
    return false;
  }, [playAudioBuffer]);
  
  // Check if MP3 files are available
  const hasMP3Files = useCallback(() => {
    return loadingStatus === 'ready' && Object.keys(audioCache.current).length > 0;
  }, [loadingStatus]);
  
  return {
    loadingStatus,
    filesLoaded,
    totalFiles,
    playLetter,
    playVoiceId,
    playCategoryWord,
    playName,
    hasMP3Files
  };
};

